import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Users, UserPlus, Edit, Trash2, Shield, Eye, Download, KeyRound } from "lucide-react";
import { useAuditLog } from "@/hooks/useAuditLog";
import { format } from "date-fns";
import { Layout } from "@/components/Layout";
import PermissionsManager from "@/components/PermissionsManager";
import NavbarPermissionsManager from "@/components/NavbarPermissionsManager";
import BannedUsersManager from "@/components/BannedUsersManager";
import { PageViewHistory } from "@/components/PageViewHistory";

interface StaffMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  team: string;
  role_level: 'user' | 'admin' | 'superadmin' | 'adviser';
  created_at: string;
  login_count?: number;
  last_login_at?: string;
}

const AdminDashboard = () => {
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<'user' | 'admin' | 'superadmin' | 'adviser' | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState<StaffMember | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newUser, setNewUser] = useState<{
    email: string; password: string; first_name: string; last_name: string;
    role: string; team: string; role_level: 'user' | 'admin' | 'superadmin' | 'adviser';
  }>({
    email: '', password: '', first_name: '', last_name: '',
    role: 'User', team: 'Advisory', role_level: 'user',
  });
  const [pageViewMember, setPageViewMember] = useState<{ id: string; name: string } | null>(null);
  const [resetPasswordMember, setResetPasswordMember] = useState<StaffMember | null>(null);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const { toast } = useToast();
  const { logStaffAccess } = useAuditLog();

  useEffect(() => {
    fetchCurrentUserRole();
    fetchStaffMembers();
  }, []);

  const fetchCurrentUserRole = async () => {
    try {
      console.log('🔐 Fetching current user role...');
      const { data, error } = await supabase.rpc('get_user_role');
      
      console.log('👤 Current user role result:', { data, error });
      
      if (error) throw error;
      setCurrentUserRole(data);
    } catch (error) {
      console.error('❌ Error fetching user role:', error);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('staff_members')
        .select(`
          *,
          profiles(login_count, last_login_at)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Flatten the data structure
      const flattenedData = data?.map(member => ({
        ...member,
        login_count: member.profiles?.[0]?.login_count || 0,
        last_login_at: member.profiles?.[0]?.last_login_at || null
      })) || [];
      
      setStaffMembers(flattenedData);
      
      // Log audit for viewing staff members (admin action)
      if (data && data.length > 0) {
        await logStaffAccess(null, 'view');
      }
    } catch (error) {
      console.error('Error fetching staff members:', error);
      toast({
        title: "Error",
        description: "Failed to load staff members",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStaffMember = async (member: StaffMember) => {
    try {
      const { error } = await supabase
        .from('staff_members')
        .update({
          first_name: member.first_name,
          last_name: member.last_name,
          role: member.role,
          team: member.team,
          role_level: member.role_level
        })
        .eq('id', member.id);

      if (error) throw error;

      // Log audit for updating staff member
      await logStaffAccess(member.id, 'update');

      toast({
        title: "Success",
        description: "Staff member updated successfully"
      });
      
      fetchStaffMembers();
      setIsEditDialogOpen(false);
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating staff member:', error);
      toast({
        title: "Error",
        description: "Failed to update staff member",
        variant: "destructive"
      });
    }
  };

  const deleteStaffMember = async (id: string) => {
    if (!confirm('Are you sure you want to delete this staff member?')) return;

    try {
      const { error } = await supabase
        .from('staff_members')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Log audit for deleting staff member
      await logStaffAccess(id, 'delete');

      toast({
        title: "Success",
        description: "Staff member deleted successfully"
      });
      
      fetchStaffMembers();
    } catch (error) {
      console.error('Error deleting staff member:', error);
      toast({
        title: "Error",
        description: "Failed to delete staff member",
        variant: "destructive"
      });
    }
  };

  const createUser = async () => {
    setCreateLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: newUser,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Success", description: `User ${newUser.email} created successfully.` });
      setIsCreateDialogOpen(false);
      setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'User', team: 'Advisory', role_level: 'user' });
      fetchStaffMembers();
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to create user", variant: "destructive" });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordMember || !resetPassword) return;
    if (resetPassword.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }

    setResetPasswordLoading(true);
    try {
      // Get user_id from profiles for this staff member
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('staff_member_id', resetPasswordMember.id)
        .single();

      if (profileError || !profileData?.user_id) {
        throw new Error('Could not find user account for this staff member.');
      }

      const { data, error } = await supabase.functions.invoke('admin-reset-password', {
        body: { target_user_id: profileData.user_id, new_password: resetPassword },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ 
        title: "Password Reset", 
        description: `Password for ${resetPasswordMember.first_name} ${resetPasswordMember.last_name} has been reset. They will be required to change it on next login.` 
      });
      setIsResetPasswordDialogOpen(false);
      setResetPasswordMember(null);
      setResetPassword('');
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to reset password", variant: "destructive" });
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const downloadAllPageViews = async () => {
    try {
      toast({
        title: "Exporting...",
        description: "Fetching all page views, this may take a moment"
      });

      // Get all page views (no limit)
      const { data: pageViews, error: pageViewsError } = await supabase
        .from('page_view_audit')
        .select('*')
        .order('timestamp', { ascending: false });

      if (pageViewsError) throw pageViewsError;

      if (!pageViews || pageViews.length === 0) {
        toast({
          title: "No Data",
          description: "No page views to export"
        });
        return;
      }

      // Get all user profiles with staff member details
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          user_id,
          staff_members(first_name, last_name, email)
        `);

      if (profilesError) throw profilesError;

      // Create a map of user_id to staff member info
      const userMap = new Map();
      profiles?.forEach(profile => {
        if (profile.staff_members) {
          userMap.set(profile.user_id, profile.staff_members);
        }
      });

      // Create CSV header
      const headers = ['Timestamp', 'User Name', 'User Email', 'Page Title', 'Page Path', 'IP Address', 'User Agent'];
      
      // Create CSV rows
      const rows = pageViews.map(view => {
        const staffMember = userMap.get(view.user_id);
        return [
          format(new Date(view.timestamp), 'PPpp'),
          staffMember ? `${staffMember.first_name} ${staffMember.last_name}` : 'Unknown',
          staffMember?.email || 'Unknown',
          view.page_title || 'Untitled',
          view.page_path,
          String(view.ip_address || 'unknown'),
          view.user_agent || 'unknown'
        ];
      });
      
      // Combine header and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `all-page-views-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Success",
        description: `Exported ${pageViews.length} page views successfully`
      });
    } catch (error) {
      console.error('Error downloading all page views:', error);
      toast({
        title: "Error",
        description: "Failed to export all page views",
        variant: "destructive"
      });
    }
  };

  const getRoleBadge = (roleLevel: string) => {
    switch (roleLevel) {
      case 'superadmin':
        return <Badge variant="destructive"><Shield className="w-3 h-3 mr-1" />Super Admin</Badge>;
      case 'admin':
        return <Badge variant="secondary"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
      case 'adviser':
        return <Badge variant="outline"><Shield className="w-3 h-3 mr-1" />Adviser</Badge>;
      default:
        return <Badge variant="outline">User</Badge>;
    }
  };

  if (loading) {
    return <Layout><div className="flex justify-center items-center h-64">Loading...</div></Layout>;
  }

  if (currentUserRole !== 'admin' && currentUserRole !== 'superadmin') {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Card className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">Manage partner members and user permissions</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Partner Members
              </CardTitle>
              <CardDescription>
                Manage partner member roles and permissions
              </CardDescription>
            </div>
            {(currentUserRole === 'admin' || currentUserRole === 'superadmin') && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create User
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadAllPageViews}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export All Page Views
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Access Level</TableHead>
                <TableHead>Login Count</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staffMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    {member.first_name} {member.last_name}
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.role}</TableCell>
                  <TableCell>{member.team}</TableCell>
                  <TableCell>{getRoleBadge(member.role_level)}</TableCell>
                  <TableCell>{member.login_count || 0}</TableCell>
                  <TableCell>
                    {member.last_login_at 
                      ? new Date(member.last_login_at).toLocaleString()
                      : 'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingMember(member);
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {(currentUserRole === 'admin' || currentUserRole === 'superadmin') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              console.log('👁️ Viewing page history for staff member:', {
                                staff_member_id: member.id,
                                name: `${member.first_name} ${member.last_name}`,
                                email: member.email
                              });
                              
                              // Get user_id from profiles for this staff member
                              const { data, error } = await supabase
                                .from('profiles')
                                .select('user_id')
                                .eq('staff_member_id', member.id)
                                .single();

                              console.log('🔍 Profile lookup result:', { 
                                data, 
                                error: error ? { message: error.message, code: error.code, details: error.details } : null 
                              });

                              if (error) {
                                console.error('❌ Error fetching user profile:', error);
                                toast({
                                  title: "Error",
                                  description: "Could not load page view history. User may not have logged in yet.",
                                  variant: "destructive"
                                });
                                return;
                              }

                              if (data?.user_id) {
                                console.log('✅ Opening page view history for user_id:', data.user_id);
                                setPageViewMember({
                                  id: data.user_id,
                                  name: `${member.first_name} ${member.last_name}`
                                });
                              } else {
                                console.warn('⚠️ No user_id found in profile');
                                toast({
                                  title: "No Data",
                                  description: "This user has no page view history yet.",
                                });
                              }
                            } catch (error: any) {
                              console.error('❌ Unexpected error:', error);
                              toast({
                                title: "Error",
                                description: "Failed to fetch page view history",
                                variant: "destructive"
                              });
                            }
                          }}
                          title="View page history"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {currentUserRole === 'superadmin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setResetPasswordMember(member);
                            setResetPassword('');
                            setIsResetPasswordDialogOpen(true);
                          }}
                          title="Reset password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                      )}
                      {currentUserRole === 'superadmin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteStaffMember(member.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {currentUserRole === 'superadmin' && <NavbarPermissionsManager />}

      {currentUserRole === 'superadmin' && <BannedUsersManager />}

      {currentUserRole === 'superadmin' && <PermissionsManager />}

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Staff Member</DialogTitle>
            <DialogDescription>
              Make changes to the staff member's information and permissions.
            </DialogDescription>
          </DialogHeader>
          {editingMember && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="firstName" className="text-right">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  value={editingMember.first_name || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, first_name: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="lastName" className="text-right">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  value={editingMember.last_name || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, last_name: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="role" className="text-right">
                  Role
                </Label>
                <Input
                  id="role"
                  value={editingMember.role}
                  onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value })}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="team" className="text-right">
                  Team
                </Label>
                <Select 
                  value={editingMember.team} 
                  onValueChange={(value) => setEditingMember({ ...editingMember, team: value })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Advisory">Advisory</SelectItem>
                    <SelectItem value="Investment">Investment</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {currentUserRole === 'superadmin' && (
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="roleLevel" className="text-right">
                    Access Level
                  </Label>
                  <Select 
                    value={editingMember.role_level} 
                     onValueChange={(value: 'user' | 'admin' | 'superadmin' | 'adviser') => 
                       setEditingMember({ ...editingMember, role_level: value })
                     }
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue />
                    </SelectTrigger>
                     <SelectContent>
                       <SelectItem value="user">User</SelectItem>
                       <SelectItem value="adviser">Adviser</SelectItem>
                       <SelectItem value="admin">Admin</SelectItem>
                       <SelectItem value="superadmin">Super Admin</SelectItem>
                     </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" onClick={() => editingMember && updateStaffMember(editingMember)}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pageViewMember && (
        <PageViewHistory
          userId={pageViewMember.id}
          userName={pageViewMember.name}
          isOpen={!!pageViewMember}
          onClose={() => setPageViewMember(null)}
        />
      )}

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create a new partner account with email and password.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newEmail" className="text-right">Email</Label>
              <Input id="newEmail" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="col-span-3" placeholder="user@company.com" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newPassword" className="text-right">Password</Label>
              <Input id="newPassword" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="col-span-3" placeholder="Min 6 characters" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newFirstName" className="text-right">First Name</Label>
              <Input id="newFirstName" value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newLastName" className="text-right">Last Name</Label>
              <Input id="newLastName" value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newRole" className="text-right">Role</Label>
              <Input id="newRole" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="col-span-3" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newTeam" className="text-right">Team</Label>
              <Select value={newUser.team} onValueChange={(value) => setNewUser({ ...newUser, team: value })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Operations">Operations</SelectItem>
                  <SelectItem value="Advisory">Advisory</SelectItem>
                  <SelectItem value="Investment">Investment</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newRoleLevel" className="text-right">Access Level</Label>
              <Select value={newUser.role_level} onValueChange={(value: 'user' | 'admin' | 'superadmin' | 'adviser') => setNewUser({ ...newUser, role_level: value })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="adviser">Adviser</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createUser} disabled={createLoading}>
              {createLoading ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordMember?.first_name} {resetPasswordMember?.last_name}. They will be required to change it on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="resetPassword" className="text-right">New Password</Label>
              <Input
                id="resetPassword"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="col-span-3"
                placeholder="Min 6 characters"
                minLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetPasswordLoading || resetPassword.length < 6}>
              {resetPasswordLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default AdminDashboard;